import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';

import { ApiError } from '../api/client';
import type { AppointmentDetail, AppointmentHandback } from '../api/types';
import { MutationError } from '../components/MutationError';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { CandidateOption } from './AppointmentForm';
import { fmtDateTime, fmtTime } from './format';
import {
  HANDBACK_URGENT_MINUTES,
  type HandbackKind,
  handbackKindLabels,
  handbackKindOptions,
  handbackResolutionLabels,
  isUrgentHandback,
  leadTimeLabel,
  minutesUntil,
} from './handback';
import { ticketStatusLabels } from './tickets';

// HandbackForm: Der zugewiesene Makler kann den Termin nicht wahrnehmen und gibt ihn an den Support zurück – er ändert
// oder löscht ihn nicht selbst. Die 180-Minuten-Regel entscheidet, ob die Rückgabe normal oder dringend ankommt.
export function HandbackForm({ appointment, onDone, onCancel }: { appointment: AppointmentDetail; onDone: (h: AppointmentHandback) => void; onCancel: () => void }) {
  const papi = useProjectApi();
  const [kind, setKind] = useState<HandbackKind>('RESCHEDULE');
  const [form, setForm] = useState({ reason: '', comment: '', availability: '' });
  const [now] = useState(() => Date.now());
  const minutes = minutesUntil(appointment.start_at, now);
  const urgent = minutes < HANDBACK_URGENT_MINUTES;
  const send = useMutation({
    mutationFn: () =>
      papi.handbackAppointment(appointment.id, {
        kind,
        reason: form.reason.trim() || undefined,
        comment: form.comment.trim() || undefined,
        availability: kind === 'CANCEL' ? undefined : form.availability.trim() || undefined,
      }),
    onSuccess: onDone,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };
  return (
    <form className="card inline-panel handback-form" onSubmit={submit} aria-label="Termin an die Terminplanung zurückgeben">
      <h2>Termin kann nicht wahrgenommen werden</h2>
      <fieldset className="field-sales">
        <legend>Was soll die Terminplanung tun?</legend>
        <ul className="candidate-list">
          {handbackKindOptions.map((o) => (
            <li key={o.kind} className={kind === o.kind ? 'candidate selected' : 'candidate'}>
              <label>
                <input type="radio" name="handback-kind" checked={kind === o.kind} onChange={() => setKind(o.kind)} />
                <span className="candidate-name">{o.label}</span>
                <span className="muted candidate-meta">{o.hint}</span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      <div className="form-grid">
        <label className="field">
          <span>Grund (optional)</span>
          <input value={form.reason} maxLength={500} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="z. B. Krankheit, Doppelbuchung" />
        </label>
        {kind !== 'CANCEL' && (
          <label className="field">
            <span>Alternative Verfügbarkeit (optional)</span>
            <input value={form.availability} maxLength={500} onChange={(e) => setForm({ ...form, availability: e.target.value })} placeholder="z. B. Donnerstag ab 14 Uhr" />
          </label>
        )}
        <label className="field wide">
          <span>Kommentar für die Terminplanung (optional)</span>
          <textarea rows={2} value={form.comment} maxLength={2000} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
        </label>
      </div>
      <p className={urgent ? 'alert alert-error' : 'alert alert-info'} role="status">
        {urgent
          ? `Der Termin beginnt ${leadTimeLabel(minutes)}. Die Rückgabe geht als dringend an die Terminplanung und wird sofort an die Projektverantwortlichen eskaliert.`
          : `Der Termin beginnt ${leadTimeLabel(minutes)}. Die Rückgabe geht an die Terminplanung, die den Kunden kontaktiert und den Termin neu organisiert.`}
      </p>
      <p className="muted">Die Terminplanung übernimmt die Abstimmung mit dem Kunden. Bis zur Entscheidung bleibt der Termin bestehen.</p>
      {send.isError && <MutationError error={send.error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={send.isPending}>
          An die Terminplanung zurückgeben
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

// HandbackBanner: Der Termin liegt beim Support. Makler sehen den Stand (und können die Rückgabe zurückziehen), der
// Support entscheidet mit den bekannten Aktionen – verschieben, anderer Makler, absagen – oder „Termin bleibt bestehen“.
export function HandbackBanner({
  appointment,
  handback: h,
  isRequester,
  onReschedule,
  onReassign,
  onCancel,
}: {
  appointment: AppointmentDetail;
  handback: AppointmentHandback;
  isRequester: boolean;
  onReschedule: () => void;
  onReassign: () => void;
  onCancel: () => void;
}) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'' | 'KEPT' | 'WITHDRAWN'>('');
  const [note, setNote] = useState('');
  const resolve = useMutation({
    mutationFn: (resolution: 'KEPT' | 'WITHDRAWN') => papi.resolveAppointmentHandback(appointment.id, { resolution, note: note.trim() || undefined }),
    onSuccess: () => {
      setMode('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
    },
  });
  const urgent = isUrgentHandback(h);
  const canPlan = can('appointment.reschedule');
  return (
    <section className={urgent ? 'alert alert-error handback-banner' : 'alert alert-warn handback-banner'} aria-label="Rückgabe an die Terminplanung">
      <strong>
        {urgent ? 'Dringend – ' : ''}An die Terminplanung zurückgegeben: {handbackKindLabels[h.kind]}
      </strong>
      <p>
        {h.requested_by_name ?? 'Makler'} am {fmtDateTime(h.requested_at)}
        {h.source === 'OUTLOOK' && ' (im Outlook-Kalender geändert)'} · Rückfrage{' '}
        <Link to={`/p/${project.key}/tickets/${h.ticket_id}`}>{h.ticket_reference}</Link> · {ticketStatusLabels[h.ticket_status]}
        {h.assigned_to_name && ` · ${h.assigned_to_name}`}
      </p>
      <dl className="facts">
        {h.reason && (
          <>
            <dt>Grund</dt>
            <dd>{h.reason}</dd>
          </>
        )}
        {h.comment && (
          <>
            <dt>Kommentar</dt>
            <dd>{h.comment}</dd>
          </>
        )}
        {h.availability && (
          <>
            <dt>Verfügbar</dt>
            <dd>{h.availability}</dd>
          </>
        )}
        {h.proposed_start_at && (
          <>
            <dt>Neue Zeit laut Outlook</dt>
            <dd>
              {fmtDateTime(h.proposed_start_at)}
              {h.proposed_end_at && `–${fmtTime(h.proposed_end_at)}`}
            </dd>
          </>
        )}
        {h.escalated_at && (
          <>
            <dt>Dringend seit</dt>
            <dd>{fmtDateTime(h.escalated_at)} (weniger als 180 Minuten bis zum Beginn)</dd>
          </>
        )}
      </dl>
      {canPlan && !isRequester && (
        <div className="form-actions">
          <button type="button" className="btn btn-small btn-primary" onClick={onReschedule}>
            Neu terminieren
          </button>
          <button type="button" className="btn btn-small" onClick={onReassign}>
            Anderen Makler zuweisen
          </button>
          {can('appointment.cancel') && (
            <button type="button" className="btn btn-small btn-danger" onClick={onCancel}>
              Absagen
            </button>
          )}
          <button type="button" className="btn btn-small btn-ghost" onClick={() => setMode('KEPT')}>
            Termin bleibt bestehen
          </button>
        </div>
      )}
      {isRequester && (
        <div className="form-actions">
          <button type="button" className="btn btn-small btn-ghost" onClick={() => setMode('WITHDRAWN')}>
            Rückgabe zurückziehen
          </button>
        </div>
      )}
      {mode && (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            resolve.mutate(mode);
          }}
        >
          <label className="field wide">
            <span>{mode === 'KEPT' ? 'Notiz (z. B. mit Kunde und Makler geklärt)' : 'Notiz (optional)'}</span>
            <input value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="submit" className="btn btn-small btn-primary" disabled={resolve.isPending}>
            {mode === 'KEPT' ? 'Bestätigen: Termin bleibt' : 'Ich nehme den Termin wahr'}
          </button>
          <button type="button" className="btn btn-small btn-ghost" onClick={() => setMode('')}>
            Abbrechen
          </button>
        </form>
      )}
      {resolve.isError && <MutationError error={resolve.error} />}
    </section>
  );
}

// ReassignPanel: Support gibt den Termin einem anderen Makler (Gebiet, Auslastung, Konflikte wie bei der Terminanlage).
export function ReassignPanel({ appointment: a, onDone, onCancel }: { appointment: AppointmentDetail; onDone: () => void; onCancel: () => void }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const [showAll, setShowAll] = useState(false);
  const [chosen, setChosen] = useState<string>();
  const [reason, setReason] = useState('');
  const [conflicts, setConflicts] = useState<string[]>([]);
  const duration = Math.round((new Date(a.end_at).getTime() - new Date(a.start_at).getTime()) / 60000);
  const suggestion = useQuery({
    queryKey: ['projects', project.id, 'reassign-suggest', a.id, a.start_at, showAll],
    queryFn: ({ signal }) =>
      papi.suggestAppointment(
        {
          lead_id: a.lead_id ?? '',
          type_id: a.type_id ?? undefined,
          start_at: a.start_at,
          duration_minutes: duration,
          street: a.street,
          house_number: a.house_number,
          postal_code: a.postal_code,
          city: a.city,
          exclude_appointment_id: a.id,
          include_out_of_area: showAll || undefined,
        },
        signal,
      ),
    enabled: !!a.lead_id,
  });
  const candidates = (suggestion.data?.candidates ?? []).filter((c) => c.user_id !== a.assigned_field_sales_user_id);
  const selected = candidates.find((c) => c.user_id === chosen);
  const outOfArea = !!selected && !selected.in_area;
  const assign = useMutation({
    mutationFn: (allowConflicts: boolean) =>
      papi.assignAppointment(a.id, { user_id: chosen ?? '', override_reason: outOfArea ? reason : undefined, allow_conflicts: allowConflicts || undefined }),
    onSuccess: onDone,
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'appointment_conflict') setConflicts(e.fieldErrors.map((f) => f.message));
    },
  });
  return (
    <form
      className="card inline-panel"
      aria-label="Anderen Makler zuweisen"
      onSubmit={(e) => {
        e.preventDefault();
        setConflicts([]);
        assign.mutate(false);
      }}
    >
      <fieldset className="field-sales">
        <legend>Anderer Makler für {fmtDateTime(a.start_at)}</legend>
        {suggestion.isPending ? (
          <p className="muted">Makler werden geprüft …</p>
        ) : candidates.length === 0 ? (
          <p className="muted">Kein anderer zuständiger Makler gefunden.</p>
        ) : (
          <ul className="candidate-list">
            {candidates.map((c) => (
              <CandidateOption key={c.user_id} candidate={c} checked={chosen === c.user_id} auto={false} onChange={() => setChosen(c.user_id)} />
            ))}
          </ul>
        )}
        {can('appointment.override_assignment') && (
          <label className="checkbox">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Auch Makler außerhalb des Gebiets zeigen
          </label>
        )}
        {outOfArea && (
          <label className="field wide">
            <span>Begründung der Ausnahme (Pflicht)</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} required />
          </label>
        )}
      </fieldset>
      {conflicts.length > 0 && (
        <div className="alert alert-warn" role="alert">
          <strong>Terminkonflikt</strong>
          <ul className="plain-list">
            {conflicts.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <button type="button" className="btn" disabled={assign.isPending} onClick={() => assign.mutate(true)}>
            Trotzdem zuweisen
          </button>
        </div>
      )}
      {assign.isError && !conflicts.length && <MutationError error={assign.error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={!chosen || assign.isPending}>
          Zuweisen
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

// AppointmentHistoryPanel: ursprünglicher Termin, wer ihn vereinbart hat, alle Änderungen (alt/neu, wer, wann, warum)
// und die Rückgaben an den Support samt Reaktion und Lösung. Die Kommunikation steht in den Rückfragen.
export function AppointmentHistoryPanel({ appointmentId, collapsed = false }: { appointmentId: string; collapsed?: boolean }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const [open, setOpen] = useState(!collapsed);
  const history = useQuery({
    queryKey: projectKeys.appointmentHistory(project.id, appointmentId),
    queryFn: ({ signal }) => papi.appointmentHistory(appointmentId, signal),
    enabled: open,
  });
  if (collapsed && !open)
    return (
      <section className="card" aria-labelledby={`history-${appointmentId}`}>
        <div className="section-head">
          <h2 id={`history-${appointmentId}`}>Verlauf</h2>
          <button type="button" className="btn btn-small btn-ghost" aria-expanded={false} onClick={() => setOpen(true)}>
            Anzeigen
          </button>
        </div>
      </section>
    );
  if (history.isPending) return null;
  if (history.isError) return <p className="muted">Der Verlauf konnte nicht geladen werden.</p>;
  const hist = history.data;
  return (
    <section className="card" aria-labelledby={`history-${appointmentId}`}>
      {collapsed ? (
        <div className="section-head">
          <h2 id={`history-${appointmentId}`}>Verlauf</h2>
          <button type="button" className="btn btn-small btn-ghost" aria-expanded onClick={() => setOpen(false)}>
            Einklappen
          </button>
        </div>
      ) : (
        <h2 id={`history-${appointmentId}`}>Verlauf</h2>
      )}
      <p className="muted">
        Ursprünglich vereinbart für {fmtDateTime(hist.original_start_at)} – am {fmtDateTime(hist.created_at)}
        {hist.created_by_name && ` von ${hist.created_by_name}`}.
      </p>
      {hist.handbacks.length > 0 && (
        <>
          <h3>Rückgaben an die Terminplanung</h3>
          <ul className="plain-list handback-history">
            {hist.handbacks.map((h) => (
              <li key={h.id}>
                <strong>{handbackKindLabels[h.kind]}</strong>
                {isUrgentHandback(h) && <span className="badge badge-error">dringend</span>}
                <span className="muted">
                  {' '}
                  · {h.requested_by_name ?? 'Makler'} am {fmtDateTime(h.requested_at)} für den Termin {fmtDateTime(h.start_at)}
                </span>
                {h.reason && <p className="muted">Grund: {h.reason}</p>}
                {h.first_response_at && (
                  <p className="muted">
                    Terminplanung reagiert: {fmtDateTime(h.first_response_at)}
                    {h.first_response_name && ` (${h.first_response_name})`}
                  </p>
                )}
                <p>
                  {h.resolution
                    ? `${handbackResolutionLabels[h.resolution]} – ${fmtDateTime(h.resolved_at)}${h.resolved_by_name ? ` (${h.resolved_by_name})` : ''}`
                    : h.open
                      ? 'Offen – die Terminplanung kümmert sich.'
                      : `Rückfrage ${ticketStatusLabels[h.ticket_status].toLowerCase()}.`}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
      {hist.entries.length > 0 && (
        <ol className="timeline" aria-label="Änderungen">
          {hist.entries.map((e, i) => (
            <li key={`${e.at}-${i}`}>
              <span className="timeline-icon" aria-hidden="true">
                •
              </span>
              <div>
                <p>
                  <strong>{e.summary}</strong>
                </p>
                <p className="muted">
                  {fmtDateTime(e.at)} · {e.actor_name ?? 'System'}
                  {e.from && e.to && ` · von ${fmtDateTime(e.from)} auf ${fmtDateTime(e.to)}`}
                  {e.reason && ` · Grund: ${e.reason}`}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
